import type { Activity } from '@/types/resources/activity.ts'

export interface ActivityIconConfig {
  classes: string
  type: string
}

/**
 * Compute activity icon configuration based on activity type and status
 * @param activity Activity object from server
 * @returns Configuration object with classes and icon type
 */
export function computeActivityIcon(activity: Activity): ActivityIconConfig {
  if (!activity) {
    return {
      classes: 'bg-surface-dark',
      type: 'document-text',
    }
  }

  switch (activity.activityType) {
    case 'subscription':
      // Different background for subscribed vs unsubscribed
      if (activity.additionalInfo?.status === 'unsubscribed') {
        return {
          classes: 'bg-surfaces-danger text-danger',
          type: 'document-list',
        }
      }
      return {
        classes: 'bg-surfaces-success text-success',
        type: 'document-list',
      }

    case 'event-attendance':
      return {
        classes: 'bg-surfaces-success text-success',
        type: 'ticket',
      }

    case 'event-purchase':
    case 'ecommerce-purchase': {
      const purchaseStatus = activity.additionalInfo?.status
      const statusClass = purchaseStatus === 'cancelled'
        ? 'bg-surfaces-neutral text-success'
        : purchaseStatus === 'refunded'
          ? 'bg-surfaces-danger'
          : 'bg-surfaces-success text-success '

      return {
        classes: statusClass,
        type: 'dollar-sign',
      }
    }

    case 'fan-message': {
      const status = activity.additionalInfo?.status
      const type = activity.additionalInfo?.provider === 'sms' ? 'sms' : 'envelope'

      if (status === 'bounced') {
        return {
          classes: 'bg-surfaces-warning text-warning',
          type,
        }
      }
      if (status === 'failed') {
        return {
          classes: 'bg-surfaces-danger text-danger',
          type,
        }
      }
      return {
        classes: 'bg-brand-light text-brand',
        type,
      }
    }

    case 'automation':
      return {
        classes: 'bg-surfaces-success',
        type: 'automation',
      }

    case 'website-activity':
      return {
        classes: 'bg-surfaces-success',
        type: 'globe-alt',
      }

    case 'link-activity':
    case 'short-link':
    case 'short-link-click':
      return {
        classes: 'bg-surfaces-success text-success',
        type: 'globe-alt',
      }

    case 'comment':
    case 'note':
      return {
        classes: 'bg-surfaces-informative',
        type: 'comment',
      }

    case 'data-capture':
    case 'campaign-registration':
      return {
        classes: 'bg-surfaces-neutral',
        type: 'campaign',
      }

    case 'profile-details':
      return {
        classes: 'bg-brand-light',
        type: 'user',
      }

    default:
      return {
        classes: 'bg-surface-dark',
        type: 'document-text',
      }
  }
}
